import { ChevronDownIcon, ChevronRightIcon, theme } from '@arnold/common';
import { removeAccents } from '@arnold/core';
import styled from '@emotion/styled';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckboxRow } from './CheckboxRow';
import { EmptySearchResult } from './EmptySearchResult';
import { HighlightText } from './HighlightText';

export interface TeamNode {
  id: string;
  name: string;
  children: TeamNode[];
  isDirectReports?: boolean;
}

const DIRECT_REPORTS_PREFIX = 'direct:';

function getDirectReportsId(teamId: string): string {
  return `${DIRECT_REPORTS_PREFIX}${teamId}`;
}

function collectAllIds(node: TeamNode): string[] {
  const ids: string[] = [node.id];
  if (node.children.length > 0) {
    ids.push(getDirectReportsId(node.id));
    for (const child of node.children) {
      ids.push(...collectAllIds(child));
    }
  }
  return ids;
}

interface TeamFilterContentProps {
  teams: TeamNode[];
  selectedTeamIds: Set<string>;
  onToggleTeam: (ids: string[]) => void;
  searchQuery: string;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}

const AUTO_EXPAND_THRESHOLD = 50;

function getTotalMemberCount(teams: TeamNode[]): number {
  return teams.reduce((sum, team) => sum + 1 + getTotalMemberCount(team.children), 0);
}

function getAllExpandableIds(teams: TeamNode[]): Set<string> {
  const ids = new Set<string>();
  const collect = (nodes: TeamNode[]) => {
    for (const node of nodes) {
      if (node.children.length > 0) {
        ids.add(node.id);
      }
      collect(node.children);
    }
  };
  collect(teams);
  return ids;
}

function getDefaultExpandedIds(teams: TeamNode[], totalCount: number): Set<string> {
  if (totalCount <= AUTO_EXPAND_THRESHOLD) {
    return getAllExpandableIds(teams);
  }
  const ids = new Set<string>();
  const expandLevel = (nodes: TeamNode[], depth: number) => {
    if (depth >= 2) return;
    for (const node of nodes) {
      if (node.children.length > 0) {
        ids.add(node.id);
        expandLevel(node.children, depth + 1);
      }
    }
  };
  expandLevel(teams, 0);
  return ids;
}

function filterTeams(teams: TeamNode[], query: string): TeamNode[] {
  if (!query) return teams;
  const normalized = removeAccents(query).toLowerCase();
  const matches = (node: TeamNode): boolean => {
    if (removeAccents(node.name).toLowerCase().includes(normalized)) return true;
    return node.children.some(matches);
  };
  return teams.filter(matches).map((node) => ({
    ...node,
    children: filterTeams(node.children, query),
  }));
}

function hasSelectedDescendant(node: TeamNode, selected: Set<string>): boolean {
  if (node.children.length > 0 && selected.has(getDirectReportsId(node.id))) return true;
  return node.children.some((child) => selected.has(child.id) || hasSelectedDescendant(child, selected));
}

function areAllDescendantsSelected(node: TeamNode, selected: Set<string>): boolean {
  if (node.children.length === 0) return selected.has(node.id);
  return (
    selected.has(getDirectReportsId(node.id)) &&
    node.children.every((child) => areAllDescendantsSelected(child, selected))
  );
}

export function useTeamExpansion(teams: TeamNode[]) {
  const totalCount = useMemo(() => getTotalMemberCount(teams), [teams]);
  const allExpandableIds = useMemo(() => getAllExpandableIds(teams), [teams]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => getDefaultExpandedIds(teams, totalCount));

  useEffect(() => {
    setExpandedIds(getDefaultExpandedIds(teams, totalCount));
  }, [teams, totalCount]);

  const allExpanded = allExpandableIds.size > 0 && expandedIds.size >= allExpandableIds.size;

  const toggleExpandAll = useCallback(() => {
    setExpandedIds(allExpanded ? new Set() : new Set(allExpandableIds));
  }, [allExpanded, allExpandableIds]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAllForSearch = useCallback((filteredTeams: TeamNode[]) => {
    setExpandedIds(getAllExpandableIds(filteredTeams));
  }, []);

  return { expandedIds, allExpanded, toggleExpandAll, toggleExpand, expandAllForSearch };
}

export const TeamFilterContent = ({
  teams,
  selectedTeamIds,
  onToggleTeam,
  searchQuery,
  expandedIds,
  onToggleExpand,
}: TeamFilterContentProps) => {
  const filteredTeams = useMemo(() => filterTeams(teams, searchQuery), [teams, searchQuery]);
  if (filteredTeams.length === 0 && searchQuery) {
    return <EmptySearchResult />;
  }

  return (
    <TeamTreeLevel
      teams={filteredTeams}
      selectedTeamIds={selectedTeamIds}
      onToggleTeam={onToggleTeam}
      expandedIds={expandedIds}
      onToggleExpand={onToggleExpand}
      searchQuery={searchQuery}
      level={0}
    />
  );
};

interface TeamTreeLevelProps {
  teams: TeamNode[];
  selectedTeamIds: Set<string>;
  onToggleTeam: (ids: string[]) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  searchQuery: string;
  level: number;
}

const TeamTreeLevel = ({
  teams,
  selectedTeamIds,
  onToggleTeam,
  expandedIds,
  onToggleExpand,
  searchQuery,
  level,
}: TeamTreeLevelProps) => {
  return (
    <>
      {teams.map((node) => {
        const isExpanded = expandedIds.has(node.id);
        const hasChildren = node.children.length > 0;
        const isFullySelected = hasChildren
          ? areAllDescendantsSelected(node, selectedTeamIds)
          : selectedTeamIds.has(node.id);
        const isPartlySelected = hasChildren && !isFullySelected && hasSelectedDescendant(node, selectedTeamIds);

        return (
          <div key={node.id}>
            <TreeRow level={level}>
              {hasChildren ? (
                <ChevronButton onClick={() => onToggleExpand(node.id)}>
                  {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                </ChevronButton>
              ) : level > 0 ? (
                <ChevronSpacer />
              ) : null}
              <RowFill>
                <CheckboxRow
                  selected={isFullySelected}
                  partlySelected={isPartlySelected}
                  label={<HighlightText text={node.name} query={searchQuery} />}
                  onClick={() => {
                    onToggleTeam(hasChildren ? collectAllIds(node) : [node.id]);
                  }}
                />
              </RowFill>
            </TreeRow>
            {hasChildren && isExpanded && (
              <>
                <TreeRow level={level + 1}>
                  <ChevronSpacer />
                </TreeRow>
                <TeamTreeLevel
                  teams={node.children}
                  selectedTeamIds={selectedTeamIds}
                  onToggleTeam={onToggleTeam}
                  expandedIds={expandedIds}
                  onToggleExpand={onToggleExpand}
                  searchQuery={searchQuery}
                  level={level + 1}
                />
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

const TreeRow = styled.div<{ level: number }>`
  display: flex;
  align-items: center;
  padding-left: ${({ level }) => level * 30}px;
`;

const RowFill = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChevronButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing.c};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-left: 5px;
  margin-right: 1px;

  svg {
    color: ${theme.colors.iconText.secondary};
  }

  &:hover svg {
    color: ${theme.colors.iconText.primary};
  }

  &:focus-visible {
    outline: ${theme.spacing.a} solid ${theme.colors.brand.primary};
    border-radius: ${theme.spacing.c};
  }
`;

const ChevronSpacer = styled.div`
  width: 32px;
  flex-shrink: 0;
`;
